import UploadClient from '@uploadcare/upload-client';
import _get from 'lodash/get';
import ApiService from '../../../util/api';
import featureFlags from '../../../util/featureFlags';

export const uploadcareKey =
  window.zola && window.zola.env === 'production' ? 'bed481ea2bef6baf44de' : 'debf625b3ed8063e511f';

const uploadcareClient = new UploadClient({ publicKey: uploadcareKey });

export const isUploadcareEnabled = featureFlags.get('uploadcareWidget') && !!uploadcareKey;

const FORMAT_TO_MIME_TYPE = {
  JPEG: 'image/jpeg',
  PNG: 'image/png',
  GIF: 'image/gif',
  SVG: 'image/svg+xml',
  TIFF: 'image/tiff',
  WEBP: 'image/webp',
  BMP: 'image/bmp',
};
const getMimeTypeFromFormat = uploadcareFile =>
  FORMAT_TO_MIME_TYPE[_get(uploadcareFile, 'imageInfo.format')];

// TODO: The retry here is a temporary fix until svc-image implements it. Remove it here after.
const MAX_COPY_ATTEMPTS = 2;
export const copyUploadcareFileToZola = (uploadcareFile, attemptNb = 0) => {
  return ApiService.post('/web-api/v1/image/uploadcare-save', {
    url: uploadcareFile.cdnUrl,
    contentType: uploadcareFile.mimeType || getMimeTypeFromFormat(uploadcareFile),
  }).then(json => {
    const data = json && json.data;

    if (!data && attemptNb < MAX_COPY_ATTEMPTS) {
      return copyUploadcareFileToZola(uploadcareFile, attemptNb + 1);
    }

    return data;
  });
};

export const copyUploadcareFileToZolaLite = uploadcareUuids => {
  return ApiService.post('/web-api/v1/image/uploadcare-save-lite', {
    uploadcareUuids,
  })
    .then(json => {
      const data = json && json.data;
      return data;
    })
    .catch(() => {});
};

export const fetchJobForCopyUploadcareFileToZolaLite = ({ jobUuid }) => {
  return ApiService.get(`/web-api/v1/image/uploadcare-save-lite/job/${jobUuid}`)
    .then(json => {
      const data = json && json.data;
      return data;
    })
    .catch(() => {});
};

export const copyUploadcareFileGroupToZola = fileGroupId => {
  return ApiService.post('/web-api/v1/image/uploadcare-group-save', {
    groupId: fileGroupId,
  }).then(json => json.data);
};

export const fetchImageMetadata = zolaImageId => {
  return ApiService.get(`/web-api/v1/image/metadata/${zolaImageId}`).then(json => json.data);
};

export const fetchBatchImageMetadata = imageUUIDs => {
  return ApiService.post(`/web-api/v1/image/metadata/list`, { imageUUIDs }).then(json => json.data);
};

export const fetchOriginalMetadata = uploadcareImageId => {
  return ApiService.get(`/web-api/v1/image/uploadcare-original/${uploadcareImageId}`)
    .then(json => json.data)
    .catch(() => ({})); // Return empty data if there is an error fetching the original
};

// Uploadcare allows us to fetch the original file data, but doesn't return modifier info,
// We must therefore manually add modiifiers to the returned uploadcare file.
export const fetchUploadcareFile = cdnUrl => {
  const urlMatches = cdnUrl.match(/ucarecdn\.com\/([^/]+)\/(-.+)?/);
  const cropMatches = cdnUrl.match(/-\/crop\/(\d+)x(\d+)\/(\d+),(\d+)/);

  const id = urlMatches[1];
  const cdnUrlModifiers = urlMatches[2];
  const crop = cropMatches
    ? {
        width: cropMatches[1],
        height: cropMatches[2],
        x: cropMatches[3],
        y: cropMatches[4],
      }
    : null;

  // Doesn't actually reupload: Simply returns existing upload's data.
  return uploadcareClient.uploadFile(id).then(uploadcareFile => {
    return {
      ...uploadcareFile,
      cdnUrl,
      cdnUrlModifiers,
      crop,
      originalImageInfo: uploadcareFile.imageInfo,
    };
  });
};

export const formatBytes = bytes => {
  const kb = 1024;
  return +(bytes / kb / kb).toFixed(2);
};

const cleanUrl = url => {
  if (!url) return url;

  let newUrl = url;

  // HACK: Uploadcare currently doesn't strip the original color profile when greyscale is applied.
  // As a temp fix, they suggested adding `max_icc_size/0` when greyscale is enabled.
  newUrl = newUrl
    .replace(/-\/max_icc_size\/0\//, '')
    .replace(/-\/grayscale\//, '-/grayscale/-/max_icc_size/0/');

  // Having the "-/preview/" param set on an image currently limits the image's max size.
  // We now strip it out to make sure we save the correctly sized image.
  newUrl = newUrl.replace(/-\/preview\//, '');

  return newUrl;
};

export const cleanUploadcareUrls = uploadcareFile => ({
  ...uploadcareFile,
  cdnUrl: cleanUrl(uploadcareFile.cdnUrl),
  cdnUrlModifiers: cleanUrl(uploadcareFile.cdnUrlModifiers),
});

const withoutCrop = url => (url || '').replace(/-\/crop\/[0-9]+x[0-9]+\/[0-9]+,[0-9]+\//, '');

export const autocropUploadcareFile = (uploadcareFile, imageElement) => {
  const {
    cdnUrlModifiers,
    crop: currentCrop,
    originalImageInfo: { height: fileHeight, width: fileWidth },
    originalUrl,
  } = uploadcareFile;
  const { height: targetHeight, width: targetWidth } = imageElement;
  const targetRatio = targetWidth / targetHeight;

  // If the files current crop already has the correct aspect ratio, let it be.
  if (currentCrop && currentCrop.width / currentCrop.height === targetRatio) {
    return uploadcareFile;
  }

  // Check if basing the crop size on image width would cause the crop height to overflow
  const canBaseCropOnImageWidth = fileWidth / targetRatio <= fileHeight;
  let height;
  let width;

  // Base the crop size on either the image's full width or full height
  if (canBaseCropOnImageWidth) {
    width = fileWidth;
    height = fileWidth / targetRatio;
  } else {
    height = fileHeight;
    width = fileHeight * targetRatio;
  }

  // Only integers are accepted in Uploadcare url modifiers, so round the cropped dimensions
  width = Math.round(width);
  height = Math.round(height);

  // Center the crop zone
  const left = Math.round((fileWidth - width) / 2);
  const top = Math.round((fileHeight - height) / 2);

  // Update cdnUrlModifiers by removing the (optional) old crop param and adding the new one
  const newModifiers = `-/crop/${width}x${height}/${left},${top}/${withoutCrop(cdnUrlModifiers)}`;

  return {
    ...uploadcareFile,
    crop: { width, height, left, top },
    cdnUrl: `${originalUrl}${newModifiers}`,
    cdnUrlModifiers: newModifiers,
  };
};



// WEBPACK FOOTER //
// ./src/components/common/UploadcareWidget/utils.js